import React, { useState } from "react";
import { Box, Button, HStack, Input, Text, VStack } from "@chakra-ui/react";
import { motion } from "framer-motion";
import type { PendingDecision } from "../flow/types";
import { stepById } from "../flow/flowGraph";

interface Props {
  decision: PendingDecision;
  onResolve: (optionId: string) => void;
}

const MotionBox = motion(Box);

const DecisionPrompt: React.FC<Props> = ({ decision, onResolve }) => {
  const step = stepById(decision.stepId);
  const [inputValue, setInputValue] = useState("");
  const inputSpec = decision.prompt.input;
  return (
    <MotionBox
      initial={{ opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      borderWidth="1px"
      borderColor="yellow.300"
      bg="yellow.50"
      borderRadius="lg"
      p={4}
    >
      <HStack justify="space-between" align="flex-start" mb={3}>
        <Box>
          <Text fontSize="10px" fontWeight="700" textTransform="uppercase" letterSpacing="0.08em" color="yellow.700">
            Decision required · {step?.name}
          </Text>
          <Text fontSize="sm" fontWeight="700" color="gray.800" mt={1}>
            {decision.prompt.title}
          </Text>
          {decision.prompt.description && (
            <Text fontSize="xs" color="gray.600" mt={1} whiteSpace="pre-wrap">
              {decision.prompt.description}
            </Text>
          )}
        </Box>
      </HStack>
      <VStack align="stretch" spacing={2}>
        {inputSpec && (
          <Box>
            <Text fontSize="10px" color="gray.600" mb={1} fontWeight="600">
              {inputSpec.label}
            </Text>
            <Input
              size="sm"
              type={inputSpec.type || "text"}
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={inputSpec.placeholder}
              bg="white"
              autoComplete="off"
            />
          </Box>
        )}
        {decision.prompt.options.map((opt) => (
          <Button
            key={opt.id}
            size="sm"
            justifyContent="flex-start"
            h="auto"
            py={2}
            whiteSpace="normal"
            textAlign="left"
            variant={opt.tone === "primary" ? "solid" : "outline"}
            colorScheme={opt.tone === "danger" ? "red" : opt.tone === "primary" ? "blue" : "gray"}
            bg={opt.tone === "primary" ? undefined : "white"}
            isDisabled={Boolean(inputSpec && opt.tone === "primary" && !inputValue.trim())}
            onClick={() =>
              onResolve(inputSpec && opt.tone === "primary" ? inputValue.trim() : opt.id)
            }
          >
            <Box>
              <Text fontSize="sm">{opt.label}</Text>
              {opt.description && (
                <Text fontSize="xs" fontWeight="400" opacity={0.8}>
                  {opt.description}
                </Text>
              )}
            </Box>
          </Button>
        ))}
      </VStack>
    </MotionBox>
  );
};

export default DecisionPrompt;
